import { SimpleCharacteristicFilterNameInStore } from '@/tsEnums/SimpleCharacteristicFilterNameInStore';
import { defineStore } from 'pinia';
import {
  CharacteristicFilterInfoObject,
  FilterInfoObjectInputVal,
} from '@/tsInterfaces/CharacteristicFilterInfoObject';
import { VariantInfo } from '@/tsClasses/VariantInfoForProteinExchange';
import { Coding } from 'fhir/r4';
import { LocationFilterInfo } from '@/tsInterfaces/LocationFilterInfo';
import { RecruitmentStatus } from '@/tsEnums/RecruitmentStatus';

export const useFilterStore = defineStore('filterStore', {
  state: () => {
    return {
      sorting: {
        sortBy: 'title',
        sortTitleDescending: false,
        sortLastUpdateDescending: false,
      },
      usrPos: {
        lat: null as null | string,
        lon: null as null | string,
        city: null as null | Coding,
      },
      usePerimeterSearch: false,
      searchPerimeter: 100,
      usePerimeterSearchSimpleMode: false,
      searchPerimeterSimpleMode: 100,
      usrPosSimpleMode: {
        lat: null as null | string,
        lon: null as null | string,
        city: null as null | Coding,
      },
      characteristicFilters: [] as Array<CharacteristicFilterInfoObject>,
      locationNameFilters: [] as Array<{
        inputVal: Coding;
        paramType: string;
      }>,
      locationNameFiltersSimpleMode: [] as Array<{
        inputVal: Coding;
        paramType: string;
      }>, //Stored separately, to avoid inconsistencies with the logical operators.
      locationAddressFilters: [] as Array<LocationFilterInfo>,
      locationAddressFiltersSimpleMode: [] as Array<LocationFilterInfo>,
      locationNameFilterLogicalOperators: [] as boolean[],
      locationAddressFilterLogicalOperators: [] as boolean[],
      characteristicFilterLogicalOperators: [] as Array<boolean>,
      statusFilters: [
        { code: RecruitmentStatus.recruiting, display: 'Recruiting' } as Coding,
        {
          code: RecruitmentStatus.notyetrecruiting,
          display: 'Not yet recruiting',
        } as Coding,
      ], //active (recruiting and not yet recruiting) as default,
      statusFiltersSimpleMode: [
        { code: RecruitmentStatus.recruiting, display: 'Recruiting' } as Coding,
        {
          code: RecruitmentStatus.notyetrecruiting,
          display: 'Not yet recruiting',
        } as Coding,
      ],
      searchIdentifier: '',
      validationFilterTag: '',
      displayHiddenStudies: false as boolean | undefined,
      validationFilterTagSimpleMode: '',
      displayHiddenStudiesSimpleMode: false as boolean | undefined,

      //Array names are defined in the enum SimpleFilterNameInStore and used for some checks in the code. This makes it less likely to break something when renaming an array.
      individualCharacteristicFilterArrays: {
        //Characteristic filters for simple mode
        [SimpleCharacteristicFilterNameInStore.Condition]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Intervention]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Subtype]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.TumorEntity]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.TumorRegion]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.TumorCategory]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Uicc]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Biomarker]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.WildtypeGene]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Variant]:
          [] as Array<CharacteristicFilterInfoObject>, //Doesn't hold system, code and display in inputVal like the others, but instead VariantInfo Objects
        [SimpleCharacteristicFilterNameInStore.MutatedGene]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Tmb]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.LineOfTherapy]:
          [] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Pathway]: [
          {
            inputVal: {
              code: '',
            },
          },
        ] as Array<CharacteristicFilterInfoObject>,
        [SimpleCharacteristicFilterNameInStore.Adjuvant]:
          [] as Array<CharacteristicFilterInfoObject>,
      },
      showOnlyGeneticallyStratifiedTrialsSimpleMode: false,
      showOnlyGeneticallyStratifiedTrialsAdvancedMode: false,
      simpleMode: true,
      queryId: 0, //Only used to make the encoded params query unique. The PaginatedList is remounted via :key="$route.query.params", so without a fresh queryId, identical filters would produce the same key and the list would not re-search, potentially showing a stale or empty page.
    };
  },

  getters: {
    areFiltersActiveSimpleMode: (state): boolean => {
      const anyIndividualCharacteristicFiltersSet = Object.keys(
        state.individualCharacteristicFilterArrays
      )
        .map((key) => state.individualCharacteristicFilterArrays[key])
        .some((array: Array<FilterInfoObjectInputVal>) =>
          array.some((filter) => filter.code && filter.code !== '')
        );

      const validationFilterActive =
        state.validationFilterTagSimpleMode !== '' &&
        state.validationFilterTagSimpleMode !== undefined;

      const locationNameFiltersActive =
        state.locationNameFiltersSimpleMode.some(
          (filter) =>
            filter.inputVal &&
            filter.inputVal.code !== '' &&
            filter.inputVal.code !== undefined
        );

      const statusFiltersActive = state.statusFiltersSimpleMode.some(
        (filter) => filter.code && filter.code !== ''
      );

      //About semantics: Technically, the filter is always set in some way. But false counts as default and therefore not actively set by user. Also, when false, no validation tag is shown. So this makes sense.
      const hiddenFilterSet = state.displayHiddenStudiesSimpleMode !== false;

      return (
        areGeneralFiltersSet() ||
        validationFilterActive ||
        state.locationAddressFiltersSimpleMode.length > 0 ||
        locationNameFiltersActive ||
        hiddenFilterSet ||
        anyIndividualCharacteristicFiltersSet ||
        statusFiltersActive ||
        state.showOnlyGeneticallyStratifiedTrialsSimpleMode
      );
    },

    areFiltersActiveAdvancedMode: (state): boolean => {
      //About semantics: Technically, the filter is always set in some way. But false counts as default and therefore not actively set by user. Also, when false, no validation tag is shown. So this makes sense.
      const hiddenFilterSet = state.displayHiddenStudies !== false;

      const statusFiltersActive = state.statusFilters.some(
        (filter) => filter.code && filter.code !== ''
      );

      const characteristicFilterSet = (
        filter: CharacteristicFilterInfoObject
      ): boolean => {
        const inputVal = filter.inputVal;
        if ('accession' in inputVal) {
          return (
            (inputVal as VariantInfo).accession !== '' &&
            (inputVal as VariantInfo).accession !== undefined
          );
        }

        if ('code' in inputVal) {
          return inputVal.code !== undefined && inputVal.code !== '';
        }

        if ('length' in inputVal) {
          return (inputVal as VariantInfo[]).some(
            (variant: VariantInfo) =>
              variant.accession !== '' && variant.accession !== undefined
          );
        } else {
          //fallback case - unable to determine type
          return false;
        }
      };

      const characteristicFiltersSet = state.characteristicFilters.some(
        (filter: CharacteristicFilterInfoObject) =>
          characteristicFilterSet(filter)
      );

      const validationFilterActive =
        state.validationFilterTag !== '' &&
        state.validationFilterTag !== undefined;

      const locationNameFiltersActive = state.locationNameFilters.some(
        (filter) =>
          filter.inputVal &&
          filter.inputVal.code !== '' &&
          filter.inputVal.code !== undefined
      );

      const locatioAddressFiltersActive = state.locationAddressFilters.some(
        (filter) =>
          filter.inputVal &&
          filter.inputVal.code !== '' &&
          filter.inputVal.code !== undefined
      );

      return (
        hiddenFilterSet ||
        characteristicFiltersSet ||
        areGeneralFiltersSet() ||
        validationFilterActive ||
        locationNameFiltersActive ||
        locatioAddressFiltersActive ||
        statusFiltersActive ||
        state.showOnlyGeneticallyStratifiedTrialsAdvancedMode
      );
    },
  },

  actions: {},
});

const areGeneralFiltersSet = (): boolean => {
  return (
    useFilterStore().usePerimeterSearch === true ||
    (useFilterStore().searchIdentifier !== '' &&
      useFilterStore().searchIdentifier !== undefined)
  );
};
